import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageBanner from "@/components/PageBanner";
import SectionHeading from "@/components/SectionHeading";
import { Package, Truck, Container, Box, MoreHorizontal, MessageSquare, FileText, Droplets, Send, RefreshCw, Globe } from "lucide-react";
import exportImage from "@/assets/export-logistics.webp";

const supportItems = [
  { icon: MessageSquare, text: "Bulk olive oil supply discussions" },
  { icon: Droplets, text: "Product profile selection" },
  { icon: Droplets, text: "Varietal and blend-oriented sourcing" },
  { icon: RefreshCw, text: "Custom blend development on request" },
  { icon: Package, text: "Sample coordination" },
  { icon: FileText, text: "Export documentation preparation" },
  { icon: Globe, text: "Support for Incoterms (EXW, FOB, CIF)" }, 
  { icon: Send, text: "International shipment coordination" },
  { icon: MessageSquare, text: "Ongoing commercial follow-up" },
];

const formats = [
  { 
    title: "IBC 1000L", 
    desc: "Suitable for flexible and smaller-volume logistics", 
    icon: Box 
  },
  { 
    title: "Flexitank 22T", 
    desc: "Standard solution for large bulk container shipments", 
    icon: Container 
  },
  { 
    title: "Tanker Truck", 
    desc: "Suitable for domestic transfer or pre-shipment logistics", 
    icon: Truck 
  },
  { 
    title: "Other Formats", 
    desc: "Available on request depending on destination and project structure", 
    icon: MoreHorizontal 
  },
];

const ExportServices = () => {
  return (
    <div>
      <PageBanner
        title="Export Services"
        subtitle="MM2A Tunisian Export supports professional buyers through a clear and structured export process. We do not only supply olive oil — we also manage the commercial and logistical framework around it."
        backgroundImage={exportImage}
      />

      {/* What We Support */}
<section className="section-padding">
  <div className="container-max">
    <SectionHeading title="What We Support" />
    {/* Changed grid to flex with justify-center */}
    <div className="flex flex-wrap justify-center gap-6">
      {supportItems.map((item, i) => (
        <div 
          key={i} 
          className="flex items-start gap-3 p-4 rounded-lg bg-muted w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(25%-1.5rem)] min-w-[250px]"
        >
          <item.icon className="w-5 h-5 text-olive mt-0.5 shrink-0" />
          <p className="text-sm text-foreground">{item.text}</p>
        </div>
      ))}
    </div>
  </div>
</section>

      {/* Supply Formats */}
      <section className="section-padding bg-muted">
        <div className="container-max">
          <SectionHeading title="Supply Formats" subtitle="We work with standard bulk export formats adapted to different commercial and logistical needs." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {formats.map((f) => (
              <div key={f.title} className="bg-card p-8 rounded-lg border border-border text-center">
                <f.icon className="w-10 h-10 text-olive mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{f.title}</h3>
                <p className="text-muted-foreground text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="section-padding">
        <div className="container-max text-center max-w-3xl mx-auto">
          <SectionHeading title="Why This Matters" />
          <p className="text-muted-foreground leading-relaxed">
            Export business requires more than product availability. It requires responsiveness, coordination and the ability to work in a structured and reliable way with professional buyers. That is exactly how we position MM2A.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground section-padding">
        <div className="container-max text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Let's Discuss Your Supply Project</h2>
          <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Tell us what profile, format or volume you are looking for, and we will help you structure the most suitable supply approach.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gold text-charcoal hover:bg-gold-light font-semibold px-10">
              Start a Discussion
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ExportServices;
